/**
 * WHICH LINK TO TRY, AND WHY — one scoring function, in plain data.
 *
 * Everything that used to decide candidate order was a hand-ordered rule with the
 * reason buried in it: the service's `score()` summed anonymous integers, the client's
 * `rankForUser` added penalties of 100 and 10, and `orderByHeadroom` sorted by bands.
 * None of them could answer "why is this one third", so the sheet could not either.
 *
 * This replaces the client half with a scoring function that returns its own working:
 * a number, and an ORDERED LIST OF REASONS, each one a `{signal, value, effect}` a
 * user interface can render without re-deriving a single rule. The weights are named
 * constants with the measurement that set them written next to them.
 *
 * ── WHAT THE EVIDENCE SAYS TO WEIGH
 * (docs/LINKS-AUDIT.md — 448 candidates across 25 titles, probed 2026-08-23)
 *
 *  1. **Whether the link is actually there** dominates everything else. 40.8 % of the
 *     ten best-ranked candidates for a title were not the film — a decoy object,
 *     usually 137–149 KB, recurring verbatim across unrelated titles. A verified
 *     answer is therefore worth more than every quality signal put together.
 *  2. **`cachedFlag` is a NEGATIVE signal, not a positive one.** 153 of 156 candidates
 *     flagged NOT cached were dead (98.1 %). Of those flagged cached, 37.3 % were dead
 *     anyway. The service scores the flag +45 for being true; the truth is "false means
 *     no, true means barely anything", and this weights it that way round.
 *  2b. **…and how little "true" means DEPENDS ON WHO SAID IT.** (docs/TORBOX-TRIAL.md
 *     § C.2, measured 2026-08-23, n = 120 per store, same afternoon, same method.)
 *     Of 120 TorBox-claimed-cached items, **120 produced a link that served real film
 *     bytes**; of 120 Real-Debrid ⚡Ready candidates, **73 did (60.8 %)**. The Wilson
 *     intervals — [96.9, 100] and [51.9, 69.1] — do not come close to touching. Roughly
 *     half of every list is TorBox rows now, so a single flat weight for the claim is
 *     one number standing in for two populations that behave nothing alike. See
 *     `STORE_CLAIMS` below: the claim is worth what the store's claims have been
 *     measured to be worth, and nothing else about the store buys anything.
 *  3. **The add-on predicts deadness better than anything in the release name.** Among
 *     cached candidates: Torrentio 22.4 % dead, MediaFusion 57.3 %, everything else
 *     96 %. Three days of production proxy logs agree — all 72 upstream failures came
 *     from one add-on host.
 *  4. **The server's rank order predicts nothing about aliveness.** Dead rate by
 *     position was flat: 40, 44, 36, 32, 36, 36, 40, 44, 56, 44 % for ranks 0..9. So
 *     the incoming order is a tie-break, not a prior.
 *  5. **Codec support is a property of THIS BROWSER**, and the blanket HEVC penalty
 *     aims the walk at the DEADER half of the corpus: among cached candidates,
 *     name-derived HEVC was 33.3 % dead against H.264's 44.7 %. `player/codecs.ts`
 *     asks the engine instead of guessing from a filename.
 *  6. **The route decides the speed, and the add-on's name does not.**
 *     (docs/SOURCES.md § A.5 — measured 2026-08-23.) The same 8 MB read did
 *     13.1–20.8 MB/s on the European POP a MediaFusion link resolves to and
 *     2.2 MB/s on the Singapore POP a Torrentio link resolves to, an 8× gap in TTFB
 *     and 6–9× in throughput, decided entirely by which server performed the debrid
 *     unrestrict. The old ranking paid Torrentio a bonus for being Torrentio, which
 *     preferred the slow route on purpose. `internal/pop.ts` times each POP on every
 *     range read, and that number arrives on `LinkCheck` — so an add-on's name is now
 *     a prior for exactly as long as it takes to measure the road.
 *  7. **Bitrate sanity is real, but the client has been measuring it wrong.** Delivered
 *     throughput, timed from the FIRST BYTE, was 56 Mbps at p50 and 89 at p90 — no
 *     crawlers at all in 30 measurements. Timed from the request, the same reads
 *     read as 25 Mbps, because 1.8–2.3 s of that is Real-Debrid's redirect chain.
 *     `player/bitrate.ts` owns the arithmetic; the engine's own fill owns the clock.
 *
 * THIS MODULE IS DATA ONLY. It renders nothing and imports no component. The source
 * sheet is another agent's territory; the contract between us is `RankedCandidate`.
 */

import type { Candidate, CheckState, QualityTier } from '$types';
import { capabilities, playabilityOf, type EngineCapabilities, type Playability } from '../player/codecs';
import { PREMIUMIZE, sourceOf, type SourceProvider } from './providers';

/* ────────────────────────────────────────────────────────────────────────────
 * The contract the UI consumes
 * ──────────────────────────────────────────────────────────────────────────── */

/** The kinds of thing that can move a candidate. Stable strings: a UI may switch on
 *  them to choose an icon, and they must not be renamed casually. */
export type RankSignal =
  | 'verified'
  | 'reputation'
  | 'cached-claim'
  /** What this store's bytes COST us, as opposed to what they are worth. */
  | 'store-cost'
  | 'source'
  /** The route the link resolved to, and what that route has been measured to do. */
  | 'route'
  | 'quality'
  | 'bitrate'
  | 'size'
  | 'codec'
  | 'language'
  | 'origin-rank'
  | 'pinned';

/** One line of the working. `effect` is in the same points as `score`. */
export interface RankReason {
  signal: RankSignal;
  /** What was observed, in words a person can read: "1080p", "not cached", "decoy". */
  value: string;
  /** How many points it moved the candidate. Signed; 0 is never emitted. */
  effect: number;
}

/**
 * The human-meaningful facts about a release, parsed once so nobody downstream has to
 * pick a filename apart again. This is deliberately structured rather than a string:
 * whoever writes the words owns the words, and this owns the truth.
 */
export interface CandidateFacts {
  /** '1080p' | '2160p' | … as the service parsed it. */
  quality: QualityTier;
  /** BluRay / WEB-DL / WEBRip / HDTV / REMUX / CAM …, or '' when the name is silent. */
  releaseType: string;
  /** 'HDR10' | 'Dolby Vision' | 'HDR' | '' */
  hdr: string;
  /** 'h264' | 'hevc' | 'av1' | 'vp9' | null when the name does not say. */
  videoCodec: string | null;
  /** 'ac3' | 'dts' | 'aac' … | null. */
  audioCodec: string | null;
  /** '5.1' | '7.1' | '2.0' | '' — from the aggregator's 🔊 line where it gave one. */
  audioChannels: string;
  /** True when the audio must be decoded to AAC-LC in the browser (a wasm chunk and
   *  a decode pass, both of which the viewer waits for at start). */
  transcodesAudio: boolean;
  /** Whether THIS browser can decode the video without help. See `Playability`. */
  playability: Playability;
  /** ISO 639-1 tags claimed by the release name, e.g. ['en','fr']; 'multi' possible. */
  languages: string[];
  /** Advertised size in bytes, 0 when unknown. */
  sizeBytes: number;
  /** MEASURED total size from the server's verification, 0 when nothing measured one.
   *  Prefer this: 42 % of advertised sizes are more than 25 % out. */
  measuredSizeBytes: number;
  /** Average bits per second, advertised or derived. 0 when unknowable. */
  bitrateBps: number;
  /** 'Torrentio' | 'MediaFusion' | … — the add-on that produced the link. */
  addon: string;
  /**
   * 'Real-Debrid' | 'TorBox' — the debrid STORE that would serve the bytes, which is a
   * different question from which add-on found the release. It is the thing whose
   * cached claim is being believed, and the two stores' claims measured 60.8 % and
   * 100 % true on the same afternoon (docs/TORBOX-TRIAL.md § C.2).
   */
  store: string;
  /** 'ThePirateBay' | '1337x' | … — the indexer behind it, '' when not stated. */
  tracker: string;
  /** Scene release group, '' when the name is not scene-shaped. */
  releaseGroup: string;
  /** Seeders, when the add-on reported any. */
  seeders: number | null;
  /**
   * The host the redirect chain ended on — the debrid POP these bytes come from,
   * e.g. `45-4.download.real-debrid.com`. '' until the server has followed the chain.
   */
  route: string;
  /** What that route has been measured to deliver, bytes per second. 0 when nobody
   *  has timed it — which is neutral, not slow. */
  routeBytesPerSec: number;
  /** Mean time to first byte on that route, ms. 0 when unmeasured. */
  routeTtfbMs: number;
}

export interface RankedCandidate {
  candidate: Candidate;
  /** Higher is tried first. Unbounded; only the order is meaningful. */
  score: number;
  /** Biggest absolute effect first, so a UI can show the top one or two and stop. */
  reasons: RankReason[];
  /** Verification state, live: 'verifying' while the server's fan-out is in flight. */
  check: CheckState;
  facts: CandidateFacts;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The weights
 * ──────────────────────────────────────────────────────────────────────────── */

export const WEIGHTS = {
  /** A link the user chose by hand. Nothing outranks a choice. */
  pinned: 10_000,

  /**
   * THE SERVER FOLLOWED THE REDIRECT CHAIN AND IT ENDED ON A HOST THAT SERVES FILMS.
   *
   * This is the only signal in the list that is EVIDENCE rather than a guess, and
   * docs/SOURCES.md § A.4 measured how good it is: across 129 probes on two sweeps
   * hours apart, the final host of the chain classified every candidate correctly —
   * zero false positives, zero misses — while the decoy RATE moved from 53 % to 14 %
   * underneath it. Design for the check, not for a rate.
   *
   * So it must outweigh EVERY name-parsed signal added together. The best a release
   * name plus a claim can manage is cachedClaim 8 + addon 20 + quality 30 + bitrate
   * 45 + size 12 + language 25 = 140, and `verifiedSlow` is above it on purpose: a
   * copy somebody has actually read beats a copy that merely reads well.
   */
  verifiedFast: 200,
  /** Same evidence, but the wake ladder had to run: real, and a slower start. */
  verifiedSlow: 160,
  /** The server touched it and got a decoy or a refusal. Conclusive. */
  verifiedDead: -1000,
  /** A touch is in flight. Neutral — it will resolve in a moment. */
  verifying: 0,

  /**
   * The service's decaying memory (`internal/reputation.ts`) already arrives as a
   * signed `standing`. These are what that standing is worth on the client, which is
   * less than a fresh verification and more than any name-derived guess.
   */
  reputationGood: 40,
  reputationBad: -60,

  /**
   * THE CACHED FLAG, WEIGHTED THE WAY IT MEASURED. 153 of 156 not-cached candidates
   * were dead (98.1 %); of the cached ones, 62.7 % were alive. So a false flag is
   * nearly a verdict and a true one is barely news — the opposite shape to the +45
   * for true / 0 for false that the service has been using.
   *
   * `notCached` is store-blind on purpose: 98.1 % dead was measured on Real-Debrid's
   * not-cached population, and nobody has probed what a TorBox "not cached" means. An
   * unmeasured population inherits the conservative number rather than a guess.
   */
  notCached: -140,

  /**
   * THE MOST A CACHED CLAIM MAY EVER BE WORTH — what a store with a spotless record
   * earns. It is not chosen; two independent constraints pin it and they agree.
   *
   *  1. **A reading must beat every claim added together.** The best a name plus a
   *     perfect claim can manage is ceiling + addonTorrentio 20 + quality 30 + bitrate
   *     45 + size 12 + language 25, and that must stay under `verifiedSlow` 160 — a
   *     copy somebody has actually read beats a copy that merely reads well, however
   *     honest its store. That puts the ceiling at **≤ 27**.
   *  2. **Real-Debrid's hand-tuned +8 must survive.** `storeClaimWeight` at a ceiling
   *     of 25 returns **8.3** for Real-Debrid's measured 60.8 % — the constant that
   *     was here before there was a second store, to within a rounding step.
   *
   * A third check, from a direction nothing else in this file uses: at 25 the gap
   * between the two stores' claims (8.3 → 25) is 16.7, which is almost exactly one
   * resolution step (1080p 30 − 720p 14 = 16). "A TorBox claim is worth about one
   * resolution step" is the sentence, and it is the right size — 39 % of the time the
   * prettier Real-Debrid row is a decoy and costs the viewer a probe.
   */
  cachedClaimCeiling: 25,

  /**
   * Per-add-on prior, from the sweep's dead rates AMONG CACHED CANDIDATES (so it is
   * not just the cached flag wearing a different hat): Torrentio 22.4 %, MediaFusion
   * 57.3 %, everything else 96 %. Small numbers on purpose — this is a prior about a
   * population, and it must never outweigh a measurement about the link in hand.
   *
   * It is a prior about DEADNESS and about nothing else, and it applies only while
   * the route is unmeasured. The moment `internal/pop.ts` has timed the POP this
   * copy resolves to, the guess from the add-on's name stands down for the number.
   */
  addonTorrentio: 20,
  addonMediafusion: 0,
  /**
   * Comet, re-admitted (docs/SOURCES.md § E.1 item 3) and measured on its own rather
   * than pooled into `other`: 7 of 18 sampled candidates served real 2.3 GB and
   * 29.3 GB files, which is 39 % live — worse than Torrentio's 70 %, better than
   * MediaFusion's 26 %, and nowhere near the 96 %-dead bucket the old `other` number
   * was measured on. It supplies 82 % of the distinct releases available, so getting
   * this one wrong by inheriting somebody else's number would matter.
   */
  addonComet: 0,
  addonOther: -20,

  /**
   * WHAT THE ROUTE HAS BEEN MEASURED TO DELIVER (docs/SOURCES.md § A.5).
   *
   * The audit's "8.8 MB/s Real-Debrid ceiling" was a Singapore/Denver number. One
   * European connection beat the whole six-connection ceiling by 2.4×, and which POP
   * you get is decided by whoever performed the unrestrict — not by the file, not by
   * the add-on's reputation, and certainly not by its name. Measured, per connection:
   * Europe 13.1–20.8 MB/s at 126–176 ms; Denver 3.5–4.0 at ~850 ms; Singapore
   * 2.2–2.3 at ~1.2 s.
   *
   * Worth more than a quality tier and less than a verification: a slow route still
   * plays the film, it just makes everyone wait. AN UNMEASURED ROUTE SCORES NOTHING —
   * never a penalty — so a POP nobody has read from yet, or a second debrid store on
   * its first evening, starts level with the ones we know.
   */
  routeFast: 40,
  routeSlow: -30,
  /** The same judgement from TTFB alone, when no read has been long enough to time
   *  a throughput. A proxy for the measurement, weighted as one. */
  routeFastTtfb: 18,
  routeSlowTtfb: -14,

  /** Resolution, once the viewer's own ceiling has been applied separately. */
  quality: { '1080p': 30, '2160p': 18, '720p': 14, '480p': 2, unknown: 8 } as Record<QualityTier, number>,
  /** Above the viewer's chosen ceiling. Not forbidden — it still plays — but last. */
  aboveQualityCeiling: -150,

  /**
   * Bitrate against what this connection has been measured to deliver. Not a band:
   * a smooth penalty, so "slightly too heavy" and "hopeless" are different answers.
   * `comfortable` earns the full bonus, `thin` earns nothing, below that it hurts.
   */
  headroomComfortable: 45,
  headroomThin: 0,
  headroomCannotCarry: -110,

  /**
   * Size sanity on top of bitrate, because a very large file is also a long wake and
   * a lot of Real-Debrid. The sweep's dead rates among cached candidates put the
   * healthiest slice at 0.7–1.5 GB (22 % dead) and the worst at >30 GB (55 %), which
   * is the same shape the streams audit's arithmetic predicted from the 8.8 MB/s
   * ceiling — a 40 GB remux is a bad pick however good it looks.
   */
  sizeSweetSpot: 12,
  sizeHuge: -35,

  /**
   * PREMIUMIZE IS A LAST RESORT, AND THIS IS THE PRICE OF ADMISSION.
   *
   * The other two stores are flat-rate: a gigabyte from Real-Debrid or TorBox costs
   * the same as not watching it. Premiumize is metered — docs/PREMIUMIZE-TRIAL.md § H
   * measured **1 GiB = 0.001 of a 1 000-point monthly allowance**, so one point is one
   * gigabyte and a 53 GB remux is 5 % of the month in a single sitting. The owner's
   * instruction (2026-08-23) is explicit: *"Premiumize should be almost like a last
   * resort when the other two don't have good options."*
   *
   * -140 is not a round number, it is THE number: the doc on `verifiedFast` above
   * computes the most a name-parsed row can ever earn as cachedClaim 8 + addon 20 +
   * quality 30 + bitrate 45 + size 12 + language 25 = **140**. Cancelling exactly that
   * gives a rule that can be stated in one sentence and checked by hand:
   *
   *   **A Premiumize row wins only when it has been READ (`verifiedFast` 200) and the
   *   alternatives have not — or when the alternatives are dead (-1000).**
   *
   * A perfect-looking TorBox row therefore still beats a perfect-looking Premiumize
   * row, which is the intent. What this must NOT do is push Premiumize below a
   * verified-dead link: -140 sits far above -1000, so a real Premiumize copy still
   * outranks a condemned one from anywhere. That is the "when the other two don't have
   * good options" half of the instruction, and it is why this is a penalty and not an
   * exclusion.
   */
  storeLastResort: -140,

  /**
   * ...AND WITHIN PREMIUMIZE, SMALLER WINS — because here size is literally money.
   *
   * `sizeHuge` already discourages a 40 GB remux on RELIABILITY grounds (55 % of
   * >30 GB candidates were dead). This is a second, orthogonal reason that applies to
   * one store only: at one point per gigabyte, choosing the 45 GB remux over the 6 GB
   * copy of the same film spends 4 % of the month to gain resolution most sofas cannot
   * resolve. 1.5 points of score per GB makes a 6 GB pick cost -9 and a 45 GB pick
   * -67.5, so within Premiumize the smaller copy wins by a wide margin, while across
   * stores it barely matters next to `storeLastResort`.
   *
   * Deliberately linear and deliberately gentle: it is a thumb on the scale for the
   * owner's points, not a quality judgement. A 2 GB copy that is the only one that
   * plays should still win, and at -3 it does.
   */
  premiumizePointsPerGb: 1.5,
  sizeSuspiciouslySmall: -60,

  /** This browser has no decoder for the codec the name claims. */
  codecUnsupported: -200,
  /** The audio needs the wasm decode: a real cost at start, not a reason to refuse. */
  codecTranscodesAudio: -8,

  /** The release claims the language the viewer asked for. */
  languageWanted: 25,
  languageMissing: -20,

  /** A cam/telesync/screener is not the film anybody meant to watch. */
  junkRelease: -300,

  /**
   * The service's own position in the list, worth a whisker. The sweep found rank had
   * NO relationship to whether a link works (flat 32–56 % dead across ranks 0–9), so
   * it survives only as a stable tie-break for candidates that are otherwise equal.
   */
  originRankStep: 0.25
} as const;

/* ────────────────────────────────────────────────────────────────────────────
 * WHOSE CLAIM IS IT — the cached flag, priced per store
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * What one store's "it's cached" has been measured to be worth.
 *
 * `rate` is the only number the weight is computed from. `low`/`high` are the 95 %
 * Wilson bounds, kept beside it because they are what says whether the ordering is a
 * real difference or a sampling artefact — the two intervals here do not overlap, and
 * `rank.test.ts` re-checks the conclusion at their worst-case ends rather than only at
 * the point estimates.
 */
export interface StoreClaimEvidence {
  /** Fraction of this store's cached claims that produced a link serving film bytes. */
  rate: number;
  /** 95 % Wilson lower and upper bound on `rate`. */
  low: number;
  high: number;
  /** Sample size behind it. */
  n: number;
  /** When it was measured, for the next person deciding whether to believe it. */
  measured: string;
}

/**
 * MEASURED CONVERSION, PER STORE (docs/TORBOX-TRIAL.md § C.2, 2026-08-23).
 *
 * Both rows were taken the same afternoon at the same sample size by the same method:
 * take a candidate the store flags cached, get a link, read 16 KB, and call it live
 * only if the answer's own `Content-Range` total clears the 10 MB decoy floor and the
 * container magic is a real one.
 *
 *  - **TorBox 120/120.** Zero decoys, zero dead. 30 of the 120 were re-probed at 50 %
 *    and at the last 16 KB and answered with the identical total, so it is not a head
 *    read being fooled by a partial cache.
 *  - **Real-Debrid 73/120.** 37 decoys and 10 timeouts. Real-Debrid deleted the
 *    endpoint that would answer honestly, so its ⚡Ready is a guess from a
 *    crowdsourced database (docs/SOURCES.md § C.2) — which is exactly why it lies.
 *
 * TO UPDATE THIS: re-run the § C.2 method and change `rate`, `low`, `high`, `n` and
 * `measured`. Nothing else in this file needs to move; the weights are computed.
 *
 * Keyed on `SourceProvider.id` from `./providers`, which is the one place that decides
 * which store a candidate belongs to.
 *
 * ── WHY THIS IS A TABLE AND NOT A ROLLING RATE THE SERVICE LEARNS
 *
 * `internal/reputation.ts` already decays outcomes per link and per cohort, and the
 * prefetch fan-out already produces exactly the verdicts a per-store rate would be fed
 * on. It was still the wrong trade, for three reasons worth writing down so nobody has
 * to re-derive them:
 *
 *  1. **A learned rate would only ever move rows the fan-out did not reach.** The
 *     moment a candidate has a verdict, `settled` retires the claim entirely and 160
 *     or 200 points of first-hand reading decide the order. What is left to improve is
 *     the unverified tail — and for that tail, 120 controlled probes per store beat a
 *     first evening's thin tally, which is what a cold learner would be running on.
 *  2. **`judge`'s cohort slot is already spoken for, and a store would take it.** It
 *     picks the BEST-ATTESTED cohort so a release group and a tracker cannot
 *     double-count the same evidence. Every candidate belongs to one of two stores and
 *     one of a dozen trackers, so a `store:` cohort would out-count `addon:` on every
 *     row and crowd out the signal the audit measured as the strongest predictor of
 *     deadness. Teaching it not to is more machinery than this win justifies.
 *  3. **This is a supplier's honesty policy, not a fast-moving quantity.** TorBox's
 *     cached set did not move by a single hash across an hour and ~180 adds
 *     (§ B.3). Real-Debrid's flag is the volatile one — 47 % to 86 % inside an
 *     afternoon — and that volatility is already inside the ±9-point interval above.
 *
 * If it ever does need to learn, the shape is: a per-store decayed tally fed from
 * `noteCheck`, `storeClaimWeight` called on the posterior instead of on `rate`, and
 * these figures as the prior with enough weight that a thin evening cannot move them.
 */
export const STORE_CLAIMS: Record<string, StoreClaimEvidence> = {
  'real-debrid': { rate: 73 / 120, low: 0.519, high: 0.691, n: 120, measured: '2026-08-23' },
  torbox: { rate: 120 / 120, low: 0.969, high: 1.0, n: 120, measured: '2026-08-23' },
  // docs/PREMIUMIZE-TRIAL.md § C: 33 claimed copies, one per title, all 33 minted a
  // link that returned 206 with real container magic. A perfect record on a THIN
  // sample — n=33 against TorBox's 120 — so the Wilson floor is 0.896, not 0.969, and
  // the claim earns less than TorBox's for exactly that reason.
  premiumize: { rate: 33 / 33, low: 0.896, high: 1.0, n: 33, measured: '2026-08-23' }
};

/**
 * THE BASE RATE OF THE LIST BEING RANKED — 186 of 448 candidates in the sweep served
 * real bytes, 41.5 % (docs/LINKS-AUDIT.md § B1, combined row: 448 probed, 262 dead).
 *
 * This is where the claim is worth NOTHING, and it has to be: a claim that leaves you
 * exactly as well-informed as knowing nothing at all has told you nothing at all. It
 * is the same rule `internal/pop.ts` keeps for routes — the unmeasured is neutral, not
 * punished — applied to the other end of the scale.
 */
const CORPUS_ALIVE_RATE = 186 / 448;

/**
 * WHAT A CACHED CLAIM IS WORTH, GIVEN HOW OFTEN THAT STORE'S CLAIMS PROVED TRUE.
 *
 * A straight line through two measured points:
 *
 *   • (41.5 %, 0)   — the base rate of the corpus. A claim no better than "this row
 *                     exists" earns nothing.
 *   • (100 %, ceiling) — a store that has never been caught lying earns the most a
 *                     claim may ever be worth, which is still far less than a read.
 *
 *     weight(rate) = ceiling × (rate − 0.415) / (1 − 0.415)
 *
 * Which gives, at the ceiling of 25 argued for in `WEIGHTS.cachedClaimCeiling`:
 *
 *   | store       | claims held |  weight |
 *   | Real-Debrid |     60.8 %  |  +8.3   |  ← the constant that was hand-tuned here
 *   | TorBox      |    100.0 %  | +25.0   |
 *
 * The line is deliberately NOT a log-odds scale, though the two constants it replaces
 * (−140 at 1.9 % and +8 at 60.8 %) sit almost exactly on one. Log-odds diverges at a
 * perfect record, and a store with 120 clean probes would price out at over +100 —
 * above what this file is willing to pay for anything nobody has actually read. The
 * ceiling is the honest way to say "a claim is not a reading, however good it is".
 *
 * Negative is possible and correct: a store whose claims proved true LESS often than
 * a coin-flip through the corpus would be actively misleading, and should say so.
 */
export function storeClaimWeight(rate: number, ceiling: number = WEIGHTS.cachedClaimCeiling): number {
  const w = (ceiling * (rate - CORPUS_ALIVE_RATE)) / (1 - CORPUS_ALIVE_RATE);
  // One decimal place: enough to keep the two stores apart, few enough digits that
  // `reasons` sum to `score` exactly and a sheet can print the number as it stands.
  return Math.round(w * 10) / 10;
}

/**
 * The claim reason for one candidate, in points and in words — or `null` when nobody
 * has measured this store's claims, which is worth nothing rather than something bad.
 *
 * The words carry the number, because "TorBox says ready" and "Real-Debrid says ready"
 * are the same sentence with wildly different meanings and a viewer cannot be expected
 * to know that.
 */
function storeClaim(store: SourceProvider): RankReason | null {
  const seen = STORE_CLAIMS[store.id];
  if (seen === undefined) return null;
  const pct = Math.round(seen.rate * 100);
  return {
    signal: 'cached-claim',
    value: `${store.label} says ready — that claim held ${pct} % of the time`,
    effect: storeClaimWeight(seen.rate)
  };
}

/** Bands, shared with `player/bitrate.ts` so one file owns the arithmetic. */
const COMFORTABLE_HEADROOM = 2.0;
const THIN_HEADROOM = 1.25;

/**
 * Where a measured route counts as fast or slow. Bytes per second and milliseconds,
 * mirroring `REPUTATION.popFastAbove` and friends in `internal/reputation.ts` — the
 * two halves of the ranking must agree about which POP is which. The three measured
 * populations are far enough apart that these sit in the gaps between them, not on
 * anybody's edge.
 */
const ROUTE_FAST_BPS = 8e6;
const ROUTE_SLOW_BPS = 3e6;
const ROUTE_FAST_TTFB_MS = 300;
const ROUTE_SLOW_TTFB_MS = 900;

const QUALITY_ORDER: Record<QualityTier, number> = {
  '2160p': 4,
  '1080p': 3,
  '720p': 2,
  '480p': 1,
  unknown: 0
};

/* ────────────────────────────────────────────────────────────────────────────
 * Parsing the facts
 * ──────────────────────────────────────────────────────────────────────────── */

const RELEASE_TYPES: Array<[RegExp, string]> = [
  [/\bremux\b/i, 'REMUX'],
  [/\b(bluray|blu-ray|bdrip|brrip|bdremux)\b/i, 'BluRay'],
  [/\b(web-?dl|webdl)\b/i, 'WEB-DL'],
  [/\b(web-?rip|webrip)\b/i, 'WEBRip'],
  [/\bhdtv\b/i, 'HDTV'],
  [/\b(dvdrip|dvd)\b/i, 'DVD'],
  [/\b(cam|camrip|hdcam)\b/i, 'CAM'],
  [/\b(ts|telesync|hdts)\b/i, 'TELESYNC'],
  [/\b(screener|scr|dvdscr)\b/i, 'SCREENER']
];

/** Everything a release name and the resolve payload will honestly give up. */
export function factsOf(c: Candidate, caps: EngineCapabilities = capabilities()): CandidateFacts {
  const name = c.title.replace(/[._]/g, ' ');
  const play = playabilityOf({ title: c.title, audioHints: c.audioHints }, caps);

  let releaseType = '';
  for (const [re, tag] of RELEASE_TYPES) {
    if (re.test(name)) {
      releaseType = tag;
      break;
    }
  }

  const hdr = /\b(dolby.?vision|\bdv\b)\b/i.test(name)
    ? 'Dolby Vision'
    : /\bhdr10\+?\b/i.test(name)
      ? 'HDR10'
      : /\bhdr\b/i.test(name)
        ? 'HDR'
        : '';

  const channels = c.audioHints.find((h) => /^\d+\.\d+$/.test(h)) ?? '';
  const originParts = (c.originLabel ?? '').split(' · ');

  return {
    quality: c.quality,
    releaseType,
    hdr,
    videoCodec: play.video,
    audioCodec: play.audio,
    audioChannels: channels,
    transcodesAudio: play.transcodesAudio,
    playability: play.playability,
    languages: [...c.langHints],
    sizeBytes: c.sizeBytes,
    measuredSizeBytes: c.check?.sizeBytes ?? 0,
    bitrateBps: c.bitrateBps,
    addon: originParts[0]?.trim() ?? '',
    tracker: originParts[1]?.trim() ?? '',
    store: sourceOf(c).label,
    releaseGroup: releaseGroupOf(c.title),
    seeders: c.seeders ?? null,
    route: c.check?.host ?? '',
    routeBytesPerSec: c.check?.popBytesPerSec ?? 0,
    routeTtfbMs: c.check?.popTtfbMs ?? 0
  };
}

/** The `-GROUP` before the extension, uppercased. '' when the name is not scene-shaped.
 *  Mirrors `releaseGroupOf` in internal/reputation.ts; both are tested. */
export function releaseGroupOf(filename: string): string {
  const m = /-([A-Za-z0-9]{2,12})(?:\[[^\]]*\])?\.(?:mkv|mp4|m4v|ts|m2ts|webm|mov)$/i.exec(filename);
  const g = m?.[1];
  if (g === undefined) return '';
  if (/^(1080p?|2160p?|720p?|480p?|x264|x265|h264|h265|hevc|avc|web|dl|rip)$/i.test(g)) return '';
  return g.toUpperCase();
}

/* ────────────────────────────────────────────────────────────────────────────
 * Scoring
 * ──────────────────────────────────────────────────────────────────────────── */

export interface RankContext {
  /** Highest resolution this viewer wants by default. Above it is last, not gone. */
  maxQuality: QualityTier;
  /** Two-letter audio language the viewer prefers, '' for no preference. */
  audioLang: string;
  /**
   * Bits per second this copy is expected to deliver. The controller passes what it
   * has MEASURED where a copy has played (`noteCalibration`), and its learned ceiling
   * everywhere else. 0 means "no opinion", which scores as neutral, not as bad.
   */
  throughputFor?: (c: Candidate) => number;
  /** Effective runtime, for turning a size into a bitrate. 0 = do not try. */
  durationSec?: number;
  /** Id of a link the user chose by hand, if any. */
  pinnedId?: string | null;
  /** This engine's decode profile. Defaults to the detected one. */
  caps?: EngineCapabilities;
}

/**
 * "checked — the file is there" is true but thin; "resolved to 45-4.download.real-
 * debrid.com — the file is there" is the same sentence with the evidence in it, and
 * the sheet can print either without knowing anything about redirect chains.
 */
function verifiedValue(host: string, what: string): string {
  return host === '' ? `checked — ${what}` : `resolved to ${host} — ${what}`;
}

/**
 * The per-add-on deadness prior, keyed on the add-on that actually produced the link.
 *
 * `provider` is a three-value enum whose `other` pools Comet with StremThru Torz, and
 * since Comet was re-admitted that bucket holds most of the list. Their measured live
 * rates are 39 % and 0-of-3 — so the LABEL decides, and the enum is the fallback for
 * a candidate whose add-on did not name itself.
 */
function addonPrior(provider: Candidate['provider'], addon: string): number {
  const a = addon.toLowerCase();
  if (a.includes('torrentio') || (a === '' && provider === 'torrentio')) return WEIGHTS.addonTorrentio;
  if (a.includes('mediafusion') || (a === '' && provider === 'mediafusion')) return WEIGHTS.addonMediafusion;
  if (a.includes('comet')) return WEIGHTS.addonComet;
  return WEIGHTS.addonOther;
}

/**
 * What the route this copy resolved to has been measured to do, in points and in
 * words — or `null` when nobody has measured it, which is the neutral answer and the
 * one every new route gets.
 *
 * Throughput is preferred to TTFB because it is what a viewer feels; TTFB stands in
 * when a route has answered but never carried a chunk long enough to time.
 */
function routeOpinion(c: Candidate): RankReason | null {
  const host = c.check?.host;
  if (host === undefined || host === '') return null;
  const bps = c.check?.popBytesPerSec ?? 0;
  if (bps > 0) {
    const mbps = (bps / 1e6).toFixed(1);
    if (bps >= ROUTE_FAST_BPS) return { signal: 'route', value: `${host} — measured ${mbps} MB/s`, effect: WEIGHTS.routeFast };
    if (bps <= ROUTE_SLOW_BPS) return { signal: 'route', value: `${host} — only ${mbps} MB/s`, effect: WEIGHTS.routeSlow };
    return null; // an ordinary route is worth nothing either way
  }
  const ttfb = c.check?.popTtfbMs ?? 0;
  if (ttfb <= 0) return null;
  if (ttfb <= ROUTE_FAST_TTFB_MS) {
    return { signal: 'route', value: `${host} answers in ${Math.round(ttfb)} ms`, effect: WEIGHTS.routeFastTtfb };
  }
  if (ttfb >= ROUTE_SLOW_TTFB_MS) {
    return { signal: 'route', value: `${host} takes ${(ttfb / 1000).toFixed(1)} s to answer`, effect: WEIGHTS.routeSlowTtfb };
  }
  return null;
}

/** The bitrate a copy needs, preferring what the server measured over what it claims. */
function neededBps(c: Candidate, facts: CandidateFacts, durationSec: number): number {
  if (c.bitrateBps > 0) return c.bitrateBps;
  const size = facts.measuredSizeBytes > 0 ? facts.measuredSizeBytes : c.sizeBytes;
  if (size > 0 && durationSec > 0) return (size * 8) / durationSec;
  return 0;
}

/**
 * Score one candidate and show the working.
 *
 * Every branch that moves the number pushes a reason, so the sum of `reasons[].effect`
 * always equals `score`. That is asserted in the tests, and it is what makes the
 * explanation trustworthy rather than decorative.
 */
export function rankOne(c: Candidate, index: number, ctx: RankContext): RankedCandidate {
  const caps = ctx.caps ?? capabilities();
  const facts = factsOf(c, caps);
  const reasons: RankReason[] = [];
  const add = (signal: RankSignal, value: string, effect: number): void => {
    if (effect !== 0) reasons.push({ signal, value, effect });
  };

  if (ctx.pinnedId !== undefined && ctx.pinnedId !== null && ctx.pinnedId === c.id) {
    add('pinned', 'you chose this one', WEIGHTS.pinned);
  }

  /* 1. Evidence first: what anybody has actually observed about these bytes. */
  const check = c.check?.state ?? 'unknown';
  /** A verdict of ANY kind — alive, slow or not-the-film — is a first-hand read, and a
   *  first-hand read settles what a claim was only guessing at. */
  const settled = check === 'fast' || check === 'slow' || check === 'dead';
  const where = c.check?.host ?? '';
  if (check === 'fast') add('verified', verifiedValue(where, 'the file is there'), WEIGHTS.verifiedFast);
  else if (check === 'slow') add('verified', verifiedValue(where, 'real, but slow to wake'), WEIGHTS.verifiedSlow);
  else if (check === 'dead') add('verified', c.why || 'checked — not the film', WEIGHTS.verifiedDead);

  if (check !== 'dead') {
    if (c.standing === 'good') add('reputation', c.why || 'has played before', WEIGHTS.reputationGood);
    else if (c.standing === 'bad') add('reputation', c.why || 'has failed before', WEIGHTS.reputationBad);
    else if (c.standing === 'dead') add('reputation', c.why || 'known bad', WEIGHTS.verifiedDead);
  }

  /*
   * 2. The store's claim — and ONLY while nobody has checked.
   *
   * `cachedFlag` is a guess from a crowdsourced database where Real-Debrid is
   * concerned, because Real-Debrid deleted the endpoint that would answer honestly
   * (docs/SOURCES.md § C.2), and a batch answer from the store's own index where
   * TorBox is. It earns its -140 as a PRIOR: 153 of 156 not-cached candidates were
   * dead. A prior that has been refuted by a first-hand read of the file is not
   * evidence any more, it is noise — so a verified copy is not dragged down by a flag
   * it has disproved, and a condemned one is not propped up by one either.
   *
   * WHICH STORE SAID IT DECIDES WHAT IT IS WORTH. Same afternoon, same method, 120
   * each: TorBox's claim converted to real film bytes 120 times out of 120,
   * Real-Debrid's 73 (docs/TORBOX-TRIAL.md § C.2). Half of every list is TorBox rows
   * now; one weight for both would be averaging two populations that behave nothing
   * alike. See `STORE_CLAIMS` and `storeClaimWeight` for the arithmetic.
   */
  if (!settled) {
    if (!c.cachedFlag) add('cached-claim', 'not cached — almost never plays', WEIGHTS.notCached);
    else {
      const claim = storeClaim(sourceOf(c));
      if (claim !== null) add('cached-claim', claim.value, claim.effect);
    }
  }

  /*
   * 2b. WHAT THIS STORE COSTS US, which is a different question from what it is worth.
   *
   * Real-Debrid and TorBox are flat-rate; Premiumize is metered at one point per
   * gigabyte out of 1 000 a month (docs/PREMIUMIZE-TRIAL.md § H). So its rows carry
   * two penalties nobody else does: a flat `storeLastResort` that keeps it behind any
   * healthy row from the other two, and a per-gigabyte cost that prefers the small
   * copy of a film over the remux of the same film. Both are unconditional — they
   * apply to verified rows too, because a verified copy still spends the points.
   */
  if (sourceOf(c).id === PREMIUMIZE.id) {
    add('store-cost', 'Premiumize — kept for when the others have nothing', WEIGHTS.storeLastResort);
    const metered = facts.measuredSizeBytes > 0 ? facts.measuredSizeBytes : c.sizeBytes;
    if (metered > 0) {
      const gb = metered / 1e9;
      add(
        'store-cost',
        `${gb.toFixed(1)} GB is ${gb.toFixed(0)} of this month's 1 000 points`,
        -Math.round(gb * WEIGHTS.premiumizePointsPerGb * 10) / 10
      );
    }
  }

  /*
   * 3. WHERE IT COMES FROM — the route if anybody has timed it, the add-on's name
   *    only while nobody has.
   *
   * These are two answers to one question and only the first is a measurement, so
   * they are mutually exclusive: a name-derived prior that survived alongside a
   * timed route would be the +8-for-Torrentio mistake wearing a different hat.
   */
  const route = routeOpinion(c);
  if (route !== null) add('route', route.value, route.effect);
  else add('source', facts.addon || c.provider, addonPrior(c.provider, facts.addon));

  /* 4. Junk the name admits to. */
  if (/\b(cam|camrip|hdcam|telesync|hdts|screener|dvdscr)\b/i.test(c.title.replace(/[._]/g, ' '))) {
    add('quality', facts.releaseType || 'cam/screener', WEIGHTS.junkRelease);
  }

  /* 5. Resolution, and the viewer's own ceiling. */
  const ceiling = QUALITY_ORDER[ctx.maxQuality] ?? 3;
  add('quality', c.quality, WEIGHTS.quality[c.quality]);
  if ((QUALITY_ORDER[c.quality] ?? 0) > ceiling) {
    add('quality', `above your ${ctx.maxQuality} setting`, WEIGHTS.aboveQualityCeiling);
  }

  /* 6. Can this connection carry it? */
  const need = neededBps(c, facts, ctx.durationSec ?? 0);
  const have = ctx.throughputFor?.(c) ?? 0;
  if (need > 0 && have > 0) {
    const headroom = have / need;
    if (headroom >= COMFORTABLE_HEADROOM) {
      add('bitrate', `${headroom.toFixed(1)}× the speed it needs`, WEIGHTS.headroomComfortable);
    } else if (headroom >= THIN_HEADROOM) {
      add('bitrate', `only ${headroom.toFixed(1)}× the speed it needs`, WEIGHTS.headroomThin);
    } else {
      add('bitrate', `too heavy for this connection (${headroom.toFixed(1)}×)`, WEIGHTS.headroomCannotCarry);
    }
  }

  /* 7. Size sanity, on the measured size where there is one. */
  const size = facts.measuredSizeBytes > 0 ? facts.measuredSizeBytes : c.sizeBytes;
  if (size > 0) {
    const gb = size / 1e9;
    if (gb > 30) add('size', `${gb.toFixed(0)} GB is a lot to stream`, WEIGHTS.sizeHuge);
    else if (gb >= 0.7 && gb <= 18) add('size', `${gb.toFixed(1)} GB`, WEIGHTS.sizeSweetSpot);
    else if (gb < 0.7) add('size', `only ${gb.toFixed(1)} GB — probably not the whole film`, WEIGHTS.sizeSuspiciouslySmall);
  }

  /* 8. What this browser can decode. */
  if (facts.playability === 'unsupported-video') {
    add('codec', `${facts.videoCodec ?? 'this codec'} — this browser cannot decode it`, WEIGHTS.codecUnsupported);
  } else if (facts.transcodesAudio) {
    add('codec', `${facts.audioCodec ?? 'audio'} is converted in the browser`, WEIGHTS.codecTranscodesAudio);
  }

  /* 9. Language. */
  const wanted = ctx.audioLang.slice(0, 2).toLowerCase();
  if (wanted !== '' && c.langHints.length > 0) {
    const has = c.langHints.some((l) => l.toLowerCase().startsWith(wanted) || l.toLowerCase() === 'multi');
    if (has) add('language', `has ${wanted}`, WEIGHTS.languageWanted);
    else add('language', `no ${wanted} track claimed`, WEIGHTS.languageMissing);
  }

  /* 10. The service's own order, as a tie-break and nothing more. */
  add('origin-rank', `#${index + 1} from the link service`, -index * WEIGHTS.originRankStep);

  const score = reasons.reduce((sum, r) => sum + r.effect, 0);
  reasons.sort((a, b) => Math.abs(b.effect) - Math.abs(a.effect));
  return { candidate: c, score: Math.round(score * 100) / 100, reasons, check, facts };
}

/**
 * Rank a whole list, best first.
 *
 * Stable: candidates that score identically keep the order the service sent, which is
 * the only tie-break with any claim to authority.
 */
export function rankCandidates(candidates: readonly Candidate[], ctx: RankContext): RankedCandidate[] {
  return candidates
    .map((c, i) => ({ ranked: rankOne(c, i, ctx), i }))
    .sort((a, b) => b.ranked.score - a.ranked.score || a.i - b.i)
    .map((x) => x.ranked);
}

/**
 * The one-line summary of why a candidate sits where it does: its strongest reason.
 * A UI that wants more takes `reasons` and writes its own sentence.
 */
export function topReason(r: RankedCandidate): RankReason | null {
  return r.reasons[0] ?? null;
}

/**
 * Candidates the walk should NOT spend a probe on, because the server already asked.
 * This is the fast path: a 16 KB touch that came back a decoy has settled the question
 * for every viewer, and re-asking it costs ~3 s of somebody's evening.
 */
export function isKnownDead(c: Candidate): boolean {
  return c.check?.state === 'dead' || c.standing === 'dead';
}

/** Candidates the server has verified as real — the client's probe will be a warm one. */
export function isPreVerified(c: Candidate): boolean {
  return c.check?.state === 'fast' || c.check?.state === 'slow';
}
